import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AGE = [
  { id: "BA30", label: "BA30 — In my 30s", color: "text-ba30" },
  { id: "BA40", label: "BA40 — In my 40s", color: "text-ba40" },
  { id: "BA50", label: "BA50 — In my 50s", color: "text-ba50" },
  { id: "BA60", label: "BA60 — 60 or better", color: "text-ba60" },
];
const ENV = [
  { id: "gym", label: "Full gym access" },
  { id: "home", label: "Home / minimal kit" },
  { id: "start", label: "Just starting — walking first" },
];
const LEVELS = [
  { id: "beginner", label: "Beginner or returning after years off" },
  { id: "returning", label: "Been training on and off" },
  { id: "regular", label: "Train regularly, want structure" },
];
const GOALS_LIST = ["Lose fat", "Build strength", "More energy", "Better sleep", "Look better", "Move better"];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [age, setAge] = useState("BA40");
  const [env, setEnv] = useState("gym");
  const [level, setLevel] = useState("returning");
  const [goals, setGoals] = useState([]);
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const toggleGoal = (g) => setGoals((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", { age_band: age, training_env: env, fitness_level: level, goals, days_per_week: days });
      await refresh();
      toast.success(`Program assigned: 10K30 ${data.track}`);
      nav("/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-16">
        <div className="label-eyebrow">Onboarding · Step {step + 1} of 4</div>
        <h1 className="wordmark mt-4 text-4xl md:text-5xl uppercase leading-none">Set Your Rebuild.</h1>

        <div className="mt-10 bg-card border border-border rounded-lg p-8">
          {step === 0 && (
            <>
              <div className="font-head uppercase text-xl">Pick Your Identity</div>
              <p className="mt-1 text-sm text-muted-foreground">Your badge on the portal — purely for community rooms.</p>
              <div className="mt-6 space-y-2">
                {AGE.map((o) => (
                  <button key={o.id} onClick={() => setAge(o.id)}
                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${age === o.id ? "bg-foreground text-background border-foreground" : "border-border hover:border-primary"}`}
                    data-testid={`onboard-age-${o.id}`}>
                    <span className={age === o.id ? "" : o.color}>●</span> <span className="ml-2">{o.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <div className="font-head uppercase text-xl">Where Will You Train?</div>
              <div className="mt-6 space-y-2">
                {ENV.map((o) => (
                  <button key={o.id} onClick={() => setEnv(o.id)}
                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${env === o.id ? "bg-foreground text-background border-foreground" : "border-border hover:border-primary"}`}
                    data-testid={`onboard-env-${o.id}`}>{o.label}</button>
                ))}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="font-head uppercase text-xl">Your Fitness Level</div>
              <div className="mt-6 space-y-2">
                {LEVELS.map((o) => (
                  <button key={o.id} onClick={() => setLevel(o.id)}
                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${level === o.id ? "bg-foreground text-background border-foreground" : "border-border hover:border-primary"}`}
                    data-testid={`onboard-level-${o.id}`}>{o.label}</button>
                ))}
              </div>
              <div className="font-head uppercase text-xl mt-8">Days Per Week You Can Train</div>
              <div className="mt-3 flex gap-2">
                {[2, 3, 4, 5].map((d) => (
                  <button key={d} onClick={() => setDays(d)}
                    className={`px-5 py-2 rounded-md text-sm border font-head uppercase tracking-wider ${days === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                    data-testid={`onboard-days-${d}`}>{d}</button>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="font-head uppercase text-xl">Your Goals</div>
              <p className="text-sm text-muted-foreground">Pick any that apply.</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {GOALS_LIST.map((g) => (
                  <button key={g} onClick={() => toggleGoal(g)}
                    className={`px-4 py-2 rounded-md text-sm border transition-colors ${goals.includes(g) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                    data-testid={`onboard-goal-${g.replace(/\s/g, "-").toLowerCase()}`}>{g}</button>
                ))}
              </div>
            </>
          )}

          <div className="mt-10 flex justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              className="btn-secondary btn-sm disabled:opacity-40" data-testid="onboard-back">Back</button>
            {step < 3 ? (
              <button onClick={() => setStep((s) => s + 1)} className="btn-primary btn-sm" data-testid="onboard-next">Next</button>
            ) : (
              <button disabled={busy} onClick={finish} className="btn-primary btn-sm disabled:opacity-60" data-testid="onboard-finish">
                {busy ? "Assigning…" : "Enter Portal"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
